import { useRef, useEffect } from 'react';
import CardContainer from '../components/CardContainer';
import ContainerLayout from '../components/ContainerLayout';
import Footer from '../components/Footer';
import Main from '../components/Main';
import TopHeader from '../components/TopHeader';
import metricsService from '../services/metrics';
import { IdPs } from '../constants/providers';
import { LAST_USED_PROVIDER_KEY } from '../constants/storage';
import { localStorageAdapter } from '../services/storage';
import { AppConfig } from '../types/app';
import { useIntl } from 'react-intl';
import LogoutInstructions from '../components/LogoutInstructions';
import { renderProviderName } from '../utils/renderProviderName';
import { ContentLayout } from '@amzn/awsui-components-react';
import globalSignOut from '../services/globalSignOut';
import builderIdShimLogout from '../services/builderIdShimLogout';

export const Logout = ({ config }: { config: AppConfig }) => {
  const { formatMessage } = useIntl();

  const { idp } = JSON.parse(
    localStorageAdapter.getItem(LAST_USED_PROVIDER_KEY) || '{}'
  );
  let providerDisplayName = idp
    ? renderProviderName(idp.idp, formatMessage)
    : undefined;

  const metricsPublisher = useRef(metricsService.getPublisher('Logout'));

  const urlParams = new URLSearchParams(window.location.search);
  const bidLogoutComplete = urlParams.get('bid_logout_complete') === 'true';

  useEffect(() => {
    metricsPublisher.current.publishCounterMonitor('Impressions', 1);
  }, []);

  useEffect(() => {
    if (bidLogoutComplete || config.enableGlobalSignOut !== 'true') return;
    const callGlobalSignOut = async (apiPath: string) => {
      await globalSignOut(apiPath);
    };
    callGlobalSignOut(config.globalSignOutApiPath).catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Error calling globalSignOut:', error);
    });
  }, [config, bidLogoutComplete]);

  useEffect(() => {
    if (
      bidLogoutComplete ||
      config.enableBuilderIdShimLogout !== 'true' ||
      !config.builderIdShimLogoutUrl ||
      idp?.idp !== IdPs.AWSBuilderID ||
      !idp?.clientId
    )
      return;

    const callBuilderIdShimLogout = async (
      shimUrl: string,
      clientId: string
    ) => {
      await builderIdShimLogout(shimUrl, clientId);
    };
    callBuilderIdShimLogout(config.builderIdShimLogoutUrl, idp.clientId).catch(
      (error) => {
        // eslint-disable-next-line no-console
        console.error('Error calling builderIdShimLogout:', error);
      }
    );
  }, [config, idp, bidLogoutComplete]);

  useEffect(() => {
    if (!providerDisplayName) {
      metricsPublisher.current.publishCounterMonitor(
        'ProviderDisplayNameNotfound',
        1
      );
    }
  }, [providerDisplayName]);

  const enableCookieComponent = Boolean(
    config?.enableCookieComponent === 'true'
  );

  return (
    <>
      <TopHeader config={config} />
      <Main config={config}>
        <ContainerLayout enableResponsiveStyles>
          <ContentLayout>
            {
              <CardContainer>
                <LogoutInstructions config={config} idp={idp?.idp as IdPs} />
              </CardContainer>
            }
          </ContentLayout>
        </ContainerLayout>
      </Main>
      <Footer enableCookieComponent={enableCookieComponent} />
    </>
  );
};
